import * as React from 'react';

import { useAtomValue } from 'jotai';

import { $inventoryTags } from '~/modules/trade/_state/loadForm.state';

type InventoryTagOption<TagType> = {
  id: string;
  label: string;
  mnemonic: string;
  group: string;
  extraInfo: string;
  tagData: TagType;
};

export const useInventoryTags = () => {
  const { data: inventoryTagsData, isLoading: isInventoryTagsLoading } =
    useAtomValue($inventoryTags);

  const inventoryTagsEntries = inventoryTagsData?.inventoryTags.entries;

  const inventoryTagsOptions = React.useMemo(() => {
    if (!inventoryTagsEntries) {
      return [];
    }

    return inventoryTagsEntries.map((tag) => ({
      id: tag.id,
      label: tag.item.name,
      mnemonic: tag.tagNumber,
      group: tag.item.itemGroup?.name ?? 'N/A',
      extraInfo: `${tag.item.name} - ${tag.netQuantity} lbs`,
      tagData: tag,
    }));
  }, [inventoryTagsEntries]);

  const inventoryTagsMapRef = React.useRef<
    Map<
      string,
      InventoryTagOption<(typeof inventoryTagsOptions)[number]['tagData']>[]
    >
  >(new Map());

  const lastInventoryTagsLength = React.useRef<number>(0);

  const inventoryTagOptionsByItemId: Map<
    string,
    InventoryTagOption<(typeof inventoryTagsOptions)[number]['tagData']>[]
  > = React.useMemo(() => {
    if (
      inventoryTagsOptions.length === lastInventoryTagsLength.current &&
      inventoryTagsMapRef.current.size > 0
    ) {
      return inventoryTagsMapRef.current;
    }

    if (!inventoryTagsOptions) {
      const emptyMap = new Map();
      inventoryTagsMapRef.current = emptyMap;
      return emptyMap;
    }

    const map = new Map();
    inventoryTagsOptions.forEach((tag) => {
      const itemId = tag.tagData.item.id;
      if (!map.has(itemId)) {
        map.set(itemId, []);
      }
      map.get(itemId).push(tag);
    });

    lastInventoryTagsLength.current = inventoryTagsOptions.length;
    inventoryTagsMapRef.current = map;
    return map;
  }, [inventoryTagsOptions]);

  return {
    inventoryTagsOptions,
    inventoryTagOptionsByItemId,
    isInventoryTagsLoading,
  };
};
